import React, { useState, useEffect } from 'react';
import { 
  Plus, Edit2, Trash2, Bot, ToggleLeft, ToggleRight, 
  MessageSquare, Building2, X, Save, CheckCircle2, Clock
} from 'lucide-react';
import api from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';

function Chatbots() {
  const { t } = useLanguage();
  
  const [chatbots, setChatbots] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);

  const [form, setForm] = useState({
    name: '',
    welcome_message: '',
    question: '',
    default_sector_id: '',
    trigger_on_new_conversation: true,
    options: []
  });

  const [newOption, setNewOption] = useState({ option: '', label: '', sector_id: '' });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [chatbotsRes, sectorsRes, statsRes] = await Promise.all([
        api.get('/api/chatbots'),
        api.get('/api/chatbots/sectors'),
        api.get('/api/chatbots/stats')
      ]);
      setChatbots(chatbotsRes.data);
      setSectors(sectorsRes.data);
      setStats(statsRes.data);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const dataToSend = {
      ...form,
      default_sector_id: form.default_sector_id === '' ? null : parseInt(form.default_sector_id)
    };
    
    try {
      if (editing) {
        await api.put(`/api/chatbots/${editing.id}`, dataToSend);
        alert('✅ ' + t('chatbots.updated'));
      } else {
        await api.post('/api/chatbots', dataToSend);
        alert('✅ ' + t('chatbots.created'));
      }
      setShowModal(false);
      setEditing(null);
      resetForm();
      loadData();
    } catch (err) {
      alert(t('common.error') + ': ' + (err.response?.data?.error || err.message));
    }
  };

  const resetForm = () => {
    setForm({
      name: '', welcome_message: '', question: '',
      default_sector_id: '', trigger_on_new_conversation: true, options: []
    });
  };

  const handleEdit = (chatbot) => {
    setEditing(chatbot);
    setForm({
      name: chatbot.name,
      welcome_message: chatbot.welcome_message,
      question: chatbot.question,
      default_sector_id: chatbot.default_sector_id || '',
      trigger_on_new_conversation: chatbot.trigger_on_new_conversation,
      options: chatbot.options || []
    });
    setShowModal(true);
  };

  const handleToggle = async (chatbot) => {
    try {
      await api.put(`/api/chatbots/${chatbot.id}`, { is_active: !chatbot.is_active });
      loadData();
    } catch (err) {
      alert(t('chatbots.errorUpdate'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('chatbots.confirmDelete'))) return;
    try {
      await api.delete(`/api/chatbots/${id}`);
      loadData();
    } catch (err) {
      alert(t('chatbots.errorDelete'));
    }
  };

  const addOption = () => {
    if (!newOption.option || !newOption.label || !newOption.sector_id) {
      alert(t('chatbots.fillAllOptionFields'));
      return;
    }
    const nextNumber = form.options.length + 1;
    setForm({
      ...form,
      options: [...form.options, {
        option: newOption.option || nextNumber.toString(),
        label: newOption.label,
        sector_id: parseInt(newOption.sector_id)
      }]
    });
    setNewOption({ option: '', label: '', sector_id: '' });
  };

  const removeOption = (index) => {
    setForm({ ...form, options: form.options.filter((_, i) => i !== index) });
  };

  const getSectorName = (sectorId) => {
    const sector = sectors.find(s => s.id === sectorId);
    return sector ? sector.name : t('chatbots.sectorNotFound');
  };

  const getNextOptionNumber = () => (form.options.length + 1).toString();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100 flex items-center gap-3">
            <Bot size={28} className="text-purple-600" />
            {t('chatbots.title')}
          </h1>
          <p className="text-gray-500 dark:text-slate-400">{t('chatbots.subtitle')}</p>
        </div>
        <button 
          onClick={() => { resetForm(); setShowModal(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 shadow-lg shadow-purple-200 dark:shadow-none">
          <Plus size={18} /> {t('chatbots.newChatbot')}
        </button>
      </div>

      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-xl">
                <MessageSquare size={20} />
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-slate-400">{t('chatbots.totalSessions')}</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-slate-100">{stats.total_sessions || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-xl">
                <CheckCircle2 size={20} />
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-slate-400">{t('chatbots.completed')}</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-slate-100">{stats.completed || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-50 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400 rounded-xl">
                <Clock size={20} />
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-slate-400">{t('chatbots.waitingResponse')}</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-slate-100">{stats.waiting || 0}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {chatbots.map(chatbot => (
          <div key={chatbot.id} className="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm hover:shadow-md transition-all">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`p-3 rounded-xl ${chatbot.is_active ? 'bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400' : 'bg-gray-50 text-gray-400'}`}>
                  <Bot size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 dark:text-slate-100 text-lg">{chatbot.name}</h3>
                  <p className="text-xs text-gray-500 dark:text-slate-400">
                    {chatbot.total_sessions || 0} {t('chatbots.sessions')} • {chatbot.completed_sessions || 0} {t('chatbots.completedLower')}
                  </p>
                </div>
              </div>
              <button 
                onClick={() => handleToggle(chatbot)}
                className={`p-1.5 rounded-lg ${chatbot.is_active ? 'text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20' : 'text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-700'}`}>
                {chatbot.is_active ? <ToggleRight size={28} /> : <ToggleLeft size={28} />}
              </button>
            </div>

            <div className="space-y-3 mb-4">
              <div className="bg-gray-50 dark:bg-slate-700 rounded-lg p-3">
                <p className="text-xs font-semibold text-gray-700 dark:text-slate-300 mb-1">💬 {t('chatbots.welcomeMessage')}:</p>
                <p className="text-sm text-gray-600 dark:text-slate-400 line-clamp-2">{chatbot.welcome_message}</p>
              </div>
              <div className="bg-gray-50 dark:bg-slate-700 rounded-lg p-3">
                <p className="text-xs font-semibold text-gray-700 dark:text-slate-300 mb-1">❓ {t('chatbots.question')}:</p>
                <p className="text-sm text-gray-600 dark:text-slate-400 line-clamp-2">{chatbot.question}</p>
              </div>
              {chatbot.options && chatbot.options.length > 0 && (
                <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3">
                  <p className="text-xs font-semibold text-purple-700 dark:text-purple-400 mb-2">📋 {t('chatbots.options')} ({chatbot.options.length}):</p>
                  <div className="space-y-1">
                    {chatbot.options.slice(0, 3).map((opt, idx) => (
                      <div key={idx} className="text-xs text-purple-900 dark:text-purple-300 flex items-center gap-2">
                        <span className="font-bold">{opt.option}️⃣</span>
                        <span>{opt.label}</span>
                        <span className="text-purple-500">→</span>
                        <span className="font-medium">{getSectorName(opt.sector_id)}</span>
                      </div>
                    ))}
                    {chatbot.options.length > 3 && (
                      <p className="text-xs text-purple-600 dark:text-purple-400 italic">+{chatbot.options.length - 3} {t('chatbots.more')}</p>
                    )}
                  </div>
                </div>
              )}
              {chatbot.default_sector_name && (
                <div className="flex items-center gap-2 text-xs">
                  <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-full font-medium flex items-center gap-1">
                    <Building2 size={12} /> {t('chatbots.default')}: {chatbot.default_sector_name}
                  </span>
                </div>
              )}
            </div>

            <div className="flex gap-2 pt-4 border-t border-gray-50 dark:border-slate-700">
              <button 
                onClick={() => handleEdit(chatbot)} 
                className="flex-1 p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg text-sm font-medium flex items-center justify-center gap-1">
                <Edit2 size={16} /> {t('common.edit')}
              </button>
              <button 
                onClick={() => handleDelete(chatbot.id)} 
                className="flex-1 p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg text-sm font-medium flex items-center justify-center gap-1">
                <Trash2 size={16} /> {t('common.delete')}
              </button>
            </div>
          </div>
        ))}
        {chatbots.length === 0 && (
          <div className="col-span-full text-center py-12 bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700">
            <Bot size={48} className="mx-auto text-gray-300 dark:text-slate-600 mb-3" />
            <p className="text-gray-400 dark:text-slate-500">{t('chatbots.noChatbots')}</p>
            <p className="text-sm text-gray-400 dark:text-slate-500 mt-1">{t('chatbots.createFirst')}</p>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-pink-600 text-white p-6 flex items-center justify-between z-10">
              <div>
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Bot size={24} />
                  {editing ? t('chatbots.editChatbot') : t('chatbots.newChatbot')}
                </h2>
                <p className="text-sm opacity-90 mt-1">{t('chatbots.configureAutoService')}</p>
              </div>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-white/20 rounded-lg">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">{t('chatbots.chatbotName')} *</label>
                <input 
                  type="text" value={form.name} onChange={e => setForm({...form, name: e.target.value})} 
                  placeholder={t('chatbots.namePlaceholder')} required
                  className="w-full p-2.5 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 outline-none" 
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                  💬 {t('chatbots.welcomeMessageLabel')} *
                </label>
                <textarea 
                  value={form.welcome_message} onChange={e => setForm({...form, welcome_message: e.target.value})} 
                  placeholder={t('chatbots.welcomePlaceholder')} rows={2} required
                  className="w-full p-2.5 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 outline-none resize-none" 
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                  ❓ {t('chatbots.questionLabel')} *
                </label>
                <textarea 
                  value={form.question} onChange={e => setForm({...form, question: e.target.value})} 
                  placeholder={t('chatbots.questionPlaceholder')} rows={2} required
                  className="w-full p-2.5 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 outline-none resize-none" 
                />
              </div>

              <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-xl p-4">
                <h3 className="text-sm font-bold text-purple-900 dark:text-purple-300 mb-3">📋 {t('chatbots.menuOptions')}</h3>
                <p className="text-xs text-purple-700 dark:text-purple-400 mb-3">{t('chatbots.menuOptionsHelp')}</p>

                {form.options.length > 0 && (
                  <div className="space-y-2 mb-3">
                    {form.options.map((option, idx) => (
                      <div key={idx} className="flex items-center gap-2 bg-white dark:bg-slate-700 p-3 rounded-lg">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-lg font-bold text-purple-600 dark:text-purple-400">{option.option}️⃣</span>
                            <span className="text-sm font-medium text-gray-900 dark:text-slate-100">{option.label}</span>
                          </div>
                          <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">→ {getSectorName(option.sector_id)}</p>
                        </div>
                        <button type="button" onClick={() => removeOption(idx)} className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded">
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="bg-white dark:bg-slate-800 rounded-lg p-3 border border-purple-200 dark:border-purple-800">
                  <p className="text-xs font-semibold text-gray-700 dark:text-slate-300 mb-2">{t('chatbots.addNewOption')}:</p>
                  <div className="grid grid-cols-3 gap-2">
                    <input type="text" value={newOption.option} onChange={e => setNewOption({...newOption, option: e.target.value})}
                      placeholder={getNextOptionNumber()} className="p-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 rounded-lg text-sm text-center" />
                    <input type="text" value={newOption.label} onChange={e => setNewOption({...newOption, label: e.target.value})}
                      placeholder={t('chatbots.optionLabelPlaceholder')} className="p-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 rounded-lg text-sm" />
                    <select value={newOption.sector_id} onChange={e => setNewOption({...newOption, sector_id: e.target.value})}
                      className="p-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 rounded-lg text-sm">
                      <option value="">{t('chatbots.selectSector')}</option>
                      {sectors.map(s => (<option key={s.id} value={s.id}>{s.name}</option>))}
                    </select>
                  </div>
                  <button type="button" onClick={addOption}
                    className="w-full mt-2 px-3 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 flex items-center justify-center gap-1">
                    <Plus size={14} /> {t('chatbots.addOption')}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                  <Building2 size={14} className="inline mr-1" /> {t('chatbots.defaultSectorLabel')}
                </label>
                <select value={form.default_sector_id} onChange={e => setForm({...form, default_sector_id: e.target.value})}
                  className="w-full p-2.5 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 outline-none">
                  <option value="">{t('chatbots.noDefaultSector')}</option>
                  {sectors.map(s => (<option key={s.id} value={s.id}>{s.name}</option>))}
                </select>
              </div>

              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.is_active !== false} onChange={e => setForm({...form, is_active: e.target.checked})}
                    className="w-4 h-4 text-purple-600 rounded" />
                  <span className="text-sm font-medium text-gray-700 dark:text-slate-300">{t('chatbots.chatbotActive')}</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.trigger_on_new_conversation} onChange={e => setForm({...form, trigger_on_new_conversation: e.target.checked})}
                    className="w-4 h-4 text-purple-600 rounded" />
                  <span className="text-sm font-medium text-gray-700 dark:text-slate-300">{t('chatbots.triggerOnNew')}</span>
                </label>
              </div>

              {form.options.length > 0 && (
                <div className="bg-gray-50 dark:bg-slate-700 rounded-xl p-4 border border-gray-200 dark:border-slate-600">
                  <h3 className="text-sm font-bold text-gray-900 dark:text-slate-100 mb-2">👁️ {t('chatbots.preview')}:</h3>
                  <div className="bg-white dark:bg-slate-800 rounded-lg p-3 text-sm text-gray-700 dark:text-slate-300 whitespace-pre-wrap">
                    {form.welcome_message || t('chatbots.welcomePlaceholder')}
                    {'\n\n'}
                    {form.question || t('chatbots.questionPlaceholder')}
                    {'\n\n'}
                    {form.options.map(opt => `${opt.option}️⃣ ${opt.label}`).join('\n')}
                    {'\n\n'}
                    <em className="text-gray-500 dark:text-slate-400">{t('chatbots.typeOptionNumber')}</em>
                  </div>
                </div>
              )}

              <div className="flex gap-3 justify-end pt-4 border-t border-gray-100 dark:border-slate-700">
                <button type="button" onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-gray-200 dark:border-slate-600 rounded-xl text-sm font-medium text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700">
                  {t('common.cancel')}
                </button>
                <button type="submit"
                  className="flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl text-sm font-bold hover:from-purple-700 hover:to-pink-700">
                  <Save size={16} />
                  {editing ? t('chatbots.saveChanges') : t('chatbots.createChatbot')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Chatbots;